/** Console-wide tenant scope: module-level store shared by the AppBar selector,
 * the router sync and every tenant-aware surface.
 *
 * Mirrors `api/debugMode.ts` / `api/tokenStorage.ts`: mutable module state + a
 * listener set, consumed from React via `useSyncExternalStore`. No provider —
 * which is also why none of the existing page tests need a new wrapper.
 *
 * Backed by `sessionStorage`, NOT `localStorage`, and deliberately NOT
 * broadcast to other tabs. `tokenStorage` does broadcast (the session is one
 * session everywhere); the scope is the opposite by requirement — an operator
 * must be able to hold two tabs open on two different tenants. If you ever add
 * a BroadcastChannel here, you have broken that.
 *
 * The scope is a FILTER, never a security boundary: it changes which records a
 * page asks for, not which records the operator may see. The backend authorizes
 * every request exactly as it did before.
 *
 * **A list of operators** (plan C3). The scope names one or more operators — a
 * client's production and sandbox (*Acme · Both*), or several clients — kept
 * sorted by id, the order of `?scope=12,845`. Two readings:
 *
 * - `useTenantScope()` / `getTenantScope()`: the scope when it names EXACTLY ONE
 *   operator, else null. Every single-tenant consumer (create dialogs,
 *   workspaces, sync pickers, the tour wizard, `tenantScope: true` grids) reads
 *   this one, so a multi-operator scope never pre-selects anything (D5).
 * - `useTenantScopeList()` / `getTenantScopeList()`: every scope operator, for
 *   the consumers that take a list (`tenantScope: 'multi'`,
 *   `usePageTenant({ multiple })`, `useScopedTenantsState`).
 *
 * Storage: `{tenants: [{id, name, code?}, …]}`. The single-object shape of the
 * first release (`{id, name, code}`) still reads, as a one-element list, so tabs
 * left open across the deploy keep their scope.
 */
import { useSyncExternalStore } from 'react';

const KEY = 'cp_console_scope';

/** Same minimal shape as `TenantPickerValue`, kept structurally compatible so
 *  a scope can be handed straight to a `<TenantPicker value=…>`. */
export interface TenantScope {
  id: number;
  name: string;
  code?: string;
}

const EMPTY: readonly TenantScope[] = Object.freeze([]);

/** One entry, or null when it is not an operator. A missing name reads `#id`
 *  (the URL sync resolves it). */
function entry(raw: unknown): TenantScope | null {
  if (!raw || typeof raw !== 'object') return null;
  const { id, name, code } = raw as Partial<TenantScope>;
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0) return null;
  return {
    id,
    name: typeof name === 'string' && name ? name : `#${id}`,
    ...(typeof code === 'string' && code ? { code } : {}),
  };
}

/** De-duplicated by id (first wins) and sorted by id. */
function normalise(list: readonly unknown[]): readonly TenantScope[] {
  const byId = new Map<number, TenantScope>();
  for (const raw of list) {
    const e = entry(raw);
    if (e && !byId.has(e.id)) byId.set(e.id, e);
  }
  if (byId.size === 0) return EMPTY;
  return Object.freeze([...byId.values()].sort((a, b) => a.id - b.id));
}

function read(): readonly TenantScope[] {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY;
    const { tenants } = parsed as { tenants?: unknown };
    // The first release stored one object; read it as a one-element list.
    return Array.isArray(tenants) ? normalise(tenants) : normalise([parsed]);
  } catch {
    return EMPTY;
  }
}

let scopeList: readonly TenantScope[] = read();
let single: TenantScope | null = scopeList.length === 1 ? (scopeList[0] ?? null) : null;

const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

const sameEntries = (a: readonly TenantScope[], b: readonly TenantScope[]) =>
  a.length === b.length &&
  a.every((x, i) => {
    const y = b[i];
    return y !== undefined && x.id === y.id && x.name === y.name && x.code === y.code;
  });

/** Every scope operator, sorted by id; empty when unscoped. Referentially stable
 *  while unchanged. */
export function getTenantScopeList(): readonly TenantScope[] {
  return scopeList;
}

/** The scope when it names exactly one operator, else null. */
export function getTenantScope(): TenantScope | null {
  return single;
}

/** Set the console-wide scope to these operators (an empty list clears it).
 *  No-op — and no notification — when nothing actually changed, so
 *  `useSyncExternalStore` snapshots don't churn. */
export function setTenantScopeList(next: readonly TenantScope[]): void {
  const list = normalise(next);
  if (sameEntries(list, scopeList)) return;
  scopeList = list;
  single = list.length === 1 ? (list[0] ?? null) : null;
  try {
    if (list.length) window.sessionStorage.setItem(KEY, JSON.stringify({ tenants: list }));
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Private mode without storage — the in-memory scope still works for this
    // page life, and the URL param carries it across navigations anyway.
  }
  notify();
}

/** Set the scope to one operator (or clear it, with null). */
export function setTenantScope(next: TenantScope | null): void {
  setTenantScopeList(next ? [next] : EMPTY);
}

export function clearTenantScope(): void {
  setTenantScopeList(EMPTY);
}

export function subscribeTenantScope(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** The console-wide scope when it names exactly one operator, else null. */
export function useTenantScope(): TenantScope | null {
  return useSyncExternalStore(subscribeTenantScope, getTenantScope, getTenantScope);
}

/** Every scope operator (empty when unscoped). */
export function useTenantScopeList(): readonly TenantScope[] {
  return useSyncExternalStore(subscribeTenantScope, getTenantScopeList, getTenantScopeList);
}

// ── "does the scope apply on this page?" ────────────────────────────────────
// Consumers (useGridFilters with a tenantScope def, usePageTenant,
// useScopedTenantState and friends) register while mounted, each with what it
// can take: 'single' (one operator at a time) or 'multi' (a list). The AppBar
// chip dims when nothing is registered, and when only single consumers are and
// the scope names several operators — so the indicator can never drift from
// reality the way a hand-maintained list of scope-aware routes would.

export type ScopeCapability = 'single' | 'multi';

interface ConsumerCounts {
  single: number;
  multi: number;
}

let counts: ConsumerCounts = { single: 0, multi: 0 };
const consumerListeners = new Set<() => void>();

function notifyConsumers() {
  for (const cb of consumerListeners) cb();
}

export function registerScopeConsumer(capability: ScopeCapability = 'single'): () => void {
  counts = { ...counts, [capability]: counts[capability] + 1 };
  notifyConsumers();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    counts = { ...counts, [capability]: counts[capability] - 1 };
    notifyConsumers();
  };
}

export function getScopeConsumerCount(): number {
  return counts.single + counts.multi;
}

export function getScopeConsumerCounts(): Readonly<ConsumerCounts> {
  return counts;
}

export function subscribeScopeConsumers(cb: () => void): () => void {
  consumerListeners.add(cb);
  return () => {
    consumerListeners.delete(cb);
  };
}

/** How the scope bears on the current page:
 *  - `applies`: something here filters by it;
 *  - `unused`: nothing on this page consumes the scope;
 *  - `single-only`: this page's consumers take one operator at a time and the
 *    scope names several, so they ignore it. */
export type ScopeApplication = 'applies' | 'unused' | 'single-only';

export function scopeApplication(c: Readonly<ConsumerCounts>, scopeSize: number): ScopeApplication {
  if (c.multi > 0) return 'applies';
  if (c.single > 0) return scopeSize > 1 ? 'single-only' : 'applies';
  return 'unused';
}

export function useScopeApplication(): ScopeApplication {
  const c = useSyncExternalStore(subscribeScopeConsumers, getScopeConsumerCounts, getScopeConsumerCounts);
  const list = useTenantScopeList();
  return scopeApplication(c, list.length);
}

/** True when something on the current page consumes the scope as it stands. */
export function useScopeApplies(): boolean {
  return useScopeApplication() === 'applies';
}
